import { getAzqpCountyAbbrev } from './azqpCountyBoundaries';
import { AZQP_COUNTY_NAMES, isAzqpCounty } from './azqpCountyBoundaries';
import { AZQP_CA_PROVINCES, AZQP_US_STATES } from './azqpRules';
import {
  CANADA_LABEL_OVERRIDES,
  TERRITORY_LABEL_POSITIONS,
  mergeFeaturesToMultiPolygon,
  transformTerritoryGeometry
} from './arrlMapUtils';

const US_STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  PR: 'Puerto Rico', VI: 'Virgin Islands'
};

const CA_PROVINCE_NAMES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland/Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut',
  ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
  TER: 'Northwest Territories'
};

// Map ARRL/RAC section slugs to AZQP 2-letter province codes for merged Canada geometry.
const CANADA_SECTION_TO_PROVINCE = {
  AB: 'AB',
  BC: 'BC',
  MB: 'MB',
  NB: 'NB',
  NL: 'NL',
  NS: 'NS',
  PE: 'PE',
  QC: 'QC',
  SK: 'SK',
  ONE: 'ON',
  ONN: 'ON',
  ONS: 'ON',
  GH: 'ON'
};

// Canada sections shown individually (not merged into provinces).
const CANADA_STANDALONE_SECTIONS = new Set(['YT', 'TER']);

// US states/territories drawn as inset shapes (same technique as the ARRL map).
const AZQP_MAP_TERRITORIES = new Set(['AK', 'HI', 'PR', 'VI']);

const TERRITORY_TRANSFORM_KEY = {
  HI: 'PAC'
};

export const AZQP_MAP_FOCUS_CENTER = [38.57, -92.57];

export const AZQP_MAP_VIEW_BOUNDS = {
  southWest: [24, -172],
  northEast: [59, -52]
};

export const AZQP_MAP_MAX_BOUNDS = {
  southWest: [24, -172],
  northEast: [78, -52]
};

export const AZQP_MAP_EMBEDDED_HALF_SPAN = {
  lat: 13,
  lon: 34
};

export function getAzqpMapFitBounds({ embedded = false } = {}) {
  const [centerLat, centerLon] = AZQP_MAP_FOCUS_CENTER;
  const factor = embedded ? 1.19 : 0.95;

  if (embedded) {
    const { lat, lon } = AZQP_MAP_EMBEDDED_HALF_SPAN;
    return {
      southWest: [centerLat - lat * factor, centerLon - lon * factor],
      northEast: [centerLat + lat * factor, centerLon + lon * factor]
    };
  }

  const { southWest, northEast } = AZQP_MAP_VIEW_BOUNDS;
  return {
    southWest: [
      centerLat + (southWest[0] - centerLat) * factor,
      centerLon + (southWest[1] - centerLon) * factor
    ],
    northEast: [
      centerLat + (northEast[0] - centerLat) * factor,
      centerLon + (northEast[1] - centerLon) * factor
    ]
  };
}

function regionName(section) {
  if (AZQP_COUNTY_NAMES[section]) {
    return `${AZQP_COUNTY_NAMES[section]} (${section})`;
  }
  if (US_STATE_NAMES[section]) {
    return US_STATE_NAMES[section];
  }
  if (CA_PROVINCE_NAMES[section]) {
    return CA_PROVINCE_NAMES[section];
  }
  return section;
}

function labelGeometry(section, mergedGeometry) {
  const labelCoordinates = TERRITORY_LABEL_POSITIONS[section]
    || CANADA_LABEL_OVERRIDES[section];

  if (labelCoordinates) {
    return { type: 'Point', coordinates: labelCoordinates };
  }

  return mergedGeometry;
}

function addMapRegion({ countyFeatures, sectionLabels, section, geometry, properties = {} }) {
  const mergedGeometry = geometry.type === 'MultiPolygon' || geometry.type === 'Polygon'
    ? geometry
    : mergeFeaturesToMultiPolygon([{ type: 'Feature', geometry }]);

  countyFeatures.push({
    type: 'Feature',
    properties: {
      section,
      name: regionName(section),
      ...properties
    },
    geometry: mergedGeometry
  });

  sectionLabels.push({
    section,
    feature: {
      type: 'Feature',
      properties: { section },
      geometry: labelGeometry(section, mergedGeometry)
    }
  });
}

export function getAzqpContactedRegions(contacts) {
  const regions = new Set();
  (contacts || []).forEach((contact) => {
    if (contact.deleted === 'Y') {
      return;
    }
    const exchange = String(contact.locationReceived || '').trim().toUpperCase();
    if (!exchange) {
      return;
    }

    if (isAzqpCounty(exchange)) {
      regions.add(exchange);
      return;
    }

    if (AZQP_US_STATES.includes(exchange) || AZQP_CA_PROVINCES.includes(exchange)) {
      regions.add(exchange);
    }

    // AZQP exchange NT maps to the TER map region.
    if (exchange === 'NT') {
      regions.add('TER');
    }
  });
  return regions;
}

export function buildAzqpMapData(countiesGeoJson, canadaGeoJson) {
  if (!countiesGeoJson?.features?.length) {
    return null;
  }

  const stateGroups = new Map();
  const azCountyGroups = new Map();
  const countyFeatures = [];
  const sectionLabels = [];

  countiesGeoJson.features.forEach((county) => {
    const state = String(county.properties?.STUSPS || '').toUpperCase();
    if (!state) {
      return;
    }

    if (state === 'AZ') {
      const abbrev = getAzqpCountyAbbrev(state, county.properties?.NAME);
      if (!abbrev) {
        return;
      }
      if (!azCountyGroups.has(abbrev)) {
        azCountyGroups.set(abbrev, []);
      }
      azCountyGroups.get(abbrev).push({ type: 'Feature', geometry: county.geometry });
      return;
    }

    const isTerritory = AZQP_MAP_TERRITORIES.has(state);
    if (!isTerritory && !AZQP_US_STATES.includes(state)) {
      return;
    }

    const geometry = isTerritory
      ? transformTerritoryGeometry(county.geometry, TERRITORY_TRANSFORM_KEY[state] || state)
      : county.geometry;

    if (!stateGroups.has(state)) {
      stateGroups.set(state, []);
    }
    stateGroups.get(state).push({ type: 'Feature', geometry });
  });

  const canadaGroups = new Map();
  (canadaGeoJson?.features || []).forEach((feature) => {
    const section = String(feature.properties?.section || '').toUpperCase();
    if (CANADA_STANDALONE_SECTIONS.has(section)) {
      addMapRegion({
        countyFeatures,
        sectionLabels,
        section,
        geometry: feature.geometry,
        properties: { isCanada: true, isStandaloneCanada: true }
      });
      return;
    }

    const province = CANADA_SECTION_TO_PROVINCE[section];
    if (!province) {
      return;
    }
    if (!canadaGroups.has(province)) {
      canadaGroups.set(province, []);
    }
    canadaGroups.get(province).push({ type: 'Feature', geometry: feature.geometry });
  });

  azCountyGroups.forEach((features, section) => {
    addMapRegion({
      countyFeatures,
      sectionLabels,
      section,
      geometry: mergeFeaturesToMultiPolygon(features),
      properties: { isAzCounty: true }
    });
  });

  stateGroups.forEach((features, section) => {
    addMapRegion({
      countyFeatures,
      sectionLabels,
      section,
      geometry: mergeFeaturesToMultiPolygon(features),
      properties: {
        isState: true,
        isTerritory: AZQP_MAP_TERRITORIES.has(section)
      }
    });
  });

  canadaGroups.forEach((features, section) => {
    addMapRegion({
      countyFeatures,
      sectionLabels,
      section,
      geometry: mergeFeaturesToMultiPolygon(features),
      properties: { isCanada: true }
    });
  });

  return {
    counties: {
      type: 'FeatureCollection',
      features: countyFeatures
    },
    sectionLabels
  };
}

export { regionName as getAzqpRegionName };
